import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useRemoteParticipants } from "@livekit/components-react";
import { useMemo } from "react";
import { useTranslate } from '@tolgee/react';
import EndConferenceConfirmationChild from "../GenericComponents/EndConferenceConfirmationChild";
import {
  CONFERENCE_ROLE_ORGANIZER,
  CONFERENCE_ROLE_PRESENTER,
  DISABLED_TRANSLATION,
  REAL_TIME_TRANSLATION,
  SEQUENCIAL_TRANSLATION,
} from "../../Constants/constantValues";
import { ReactComponent as RealTime } from "../../Assets/iconsv2/controlButtons/TranslateRealTime.svg"
import { ReactComponent as Sequencial } from "../../Assets/iconsv2/controlButtons/TranslateSequencial.svg"
import { ReactComponent as Disabled } from "../../Assets/iconsv2/controlButtons/TranslateDisabled.svg"
import { ReactComponent as AiIcon } from "../../Assets/iconsv2/generic/sparkles.svg"
import { FileText } from "lucide-react";
import { useConferencesService } from "../../Context/ConferenceContext";
const CallHeader = ({
  callDetails,
  handleSwitchGrid,
  translationEnabled,
  translationType,
  handleSelectTranslationType,
  translationModeChangePending = false,
  callStartTime,
  showEndConferenceBtn = false,
  handleEndConferenceBtn,
  isRecording = false,
  aiSummaryEnabled = false,
  transcriptExportEnabled = false,
  canChangeTranslationType = true,
}) => {

  const remoteParticipants = useRemoteParticipants();
  const { currentConferenceRef } = useConferencesService();
  const [duration, setDuration] = useState(0);
  const [endConferenceOpen, setEndConferenceOpen] = useState(false);

  const remoteParticipantsWithoutAgent = useMemo(() => {
    return remoteParticipants.filter((participant) => {
      if (!participant?.identity) {
        return false;
      }

      if (participant.identity.includes("stt-agent")) {
        return false;
      }

      return !participant.identity.startsWith("agent_emotii|");
    });
  }, [remoteParticipants]);

  const displayHeaderName = () => {
    if (callDetails?.conference_id) {
      const conferenceFromContext = currentConferenceRef?.current;
      return (
        callDetails?.conference?.title ||
        conferenceFromContext?.title
      );
    }
    if (callDetails?.meeting_id) {
      return callDetails?.meeting_name;
    }
    if (callDetails?.group) {
      return callDetails?.group?.name;
    } else {
      let string = remoteParticipantsWithoutAgent.map((user) => user.name).join(", ");
      return string;
    }
  };

  const formatDuration = (seconds) => {

    if (!seconds || seconds < 0 || isNaN(seconds)) {
      return '00:00';
    }

    seconds = Math.floor(seconds);

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {    
    if (!callStartTime) {
      setDuration(0);
      return;
    }
    
    const updateDuration = () => {
      const elapsed = Math.floor((Date.now() - callStartTime) / 1000);
      setDuration(elapsed);
    };
    
    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    
    return () => clearInterval(interval);
  }, [callStartTime]);

  const { t } = useTranslate();
  const isConference = !!callDetails?.conference_id;
  const canSwitchConferenceTranslation =
    !isConference ||
    [CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER].includes(
      callDetails?.conference_role
    );

  const onEndConferenceClick = () => {
    if (!handleEndConferenceBtn) {
      return;
    }
    setEndConferenceOpen(true);
  };

  const handleConfirmEndConference = () => {
    if (handleEndConferenceBtn) {
      handleEndConferenceBtn();
    }
  };

  return (
    <Container>
      <NameWrapper>
        <Name>{displayHeaderName()}</Name>
        <DurationWrapper>
          <PulsingDot />
          <Duration>{formatDuration(duration)}</Duration>
        </DurationWrapper>
      </NameWrapper>

      <EndConferenceConfirmationChild
        open={endConferenceOpen}
        setOpen={setEndConferenceOpen}
        onConfirm={handleConfirmEndConference}
      />

      <RightControls>
        {((callDetails?.meeting_id && (aiSummaryEnabled || transcriptExportEnabled)) || isRecording) && (
          <StatusIndicators>
            {callDetails?.meeting_id && aiSummaryEnabled && (
              <AiSummaryIndicator title={t('call.ai_summary_active')}>
                <AiIcon />
              </AiSummaryIndicator>
            )}

            {callDetails?.meeting_id && transcriptExportEnabled && (
              <AiSummaryIndicator title={t('call.transcription_active')}>
                <FileText />
              </AiSummaryIndicator>
            )}

            {isRecording && (
              <RecordingIndicator title={t('call_modal.recording_in_progress')}>
                <RecordingDot />
                <span>{t('call_modal.recording')}</span>
              </RecordingIndicator>
            )}
          </StatusIndicators>
        )}

        {isConference && showEndConferenceBtn && (
          <EndConferenceButton type="button" onClick={onEndConferenceClick}>
            {t('conferences.end_conference')}
          </EndConferenceButton>
        )}

        {canSwitchConferenceTranslation && (
          <TranslationSwitchWrapper>
            <TranslationSwitchButtonWrapper>
              <TranslationSwitchButton selected={translationType === REAL_TIME_TRANSLATION}
                title={t('call_modal.real_time')}
                onClick={() => canChangeTranslationType && handleSelectTranslationType(REAL_TIME_TRANSLATION)}
                disabled={!canChangeTranslationType}
              >
                <RealTime />
                <span>{t('call_modal.real_time')}</span>
              </TranslationSwitchButton>
              <TranslationSwitchButton selected={translationType === SEQUENCIAL_TRANSLATION}
                title={t('call_modal.sequencial')}
                onClick={() => canChangeTranslationType && handleSelectTranslationType(SEQUENCIAL_TRANSLATION)}
                disabled={!canChangeTranslationType}
              >
                <Sequencial />
                <span>{t('call_modal.sequencial')}</span>
              </TranslationSwitchButton>
              <TranslationSwitchButton selected={translationType === DISABLED_TRANSLATION}
                title={t('general.disabled')}
                onClick={() => canChangeTranslationType && handleSelectTranslationType(DISABLED_TRANSLATION)}
                disabled={!canChangeTranslationType}
              >
                <Disabled />
                <span>{t('general.disabled')}</span>
             </TranslationSwitchButton>
            </TranslationSwitchButtonWrapper>
          </TranslationSwitchWrapper>
        )}
      </RightControls>
    </Container>
  );
};

export default CallHeader;

const Container = styled.div`
  display: flex;
  width: 100%;
  padding: 10px 20px;
  align-items: center;
  gap: 20px;
  container: callHeader / inline-size;
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  margin-left: 20px;
  flex: 1;
  min-width: 0;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: auto;
`;

const StatusIndicators = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Name = styled.h3`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
`;

const DurationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
`;

const PulsingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const Duration = styled.p`
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  opacity: 0.7;
  margin: 0;
  letter-spacing: 0.3px;
`;

const GridButton = styled.button`
  display: flex;
  margin-left: auto;
  cursor: pointer;
  background: none;
  border: none;
  svg {
    max-width: 30px;
    color: ${({ theme }) => theme.menu.groupContentBackground};
    fill: ${({ theme }) => theme.menu.primaryTextColor};
  }
`;

const TranslationSwitchWrapper = styled.div`
display:flex;
align-items:center;
overflow:hidden;
border-radius:4px;
gap:15px;
padding:9px 0px;
`

const TranslationSwitchButtonWrapper = styled.div`
display:flex;
align-items:center;
overflow:hidden;
border-radius:4px;
`
const EndConferenceButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  background: #ef4444;
  border: 1px solid #dc2626;
  color: white;
  border-radius: 4px;
  min-height: 36px;
  padding: 9px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;

  &:hover {
    background: #dc2626;
  }
`;

const TranslationSwitchButton = styled.button`
display:flex;
align-items:center;
justify-content:center;
padding:9px 14px;
gap:6px;
background:${({ theme }) => theme.callComponent.participantsListBackground};
border:none;
color:${({ theme }) => theme.menu.infoText};
font-size:14px;
font-weight:400;
cursor:pointer;
white-space:nowrap;

svg{
flex-shrink:0;
}

span{
display:none;
}

@container callHeader (min-width: 820px){
span{
display:inline;
}
}

${({ selected }) => selected && `
span{
display:inline;
}
`}

&:first-child{
border-right:1px solid ${({ theme }) => theme.sidebar.borderColor};

}
&:last-child{
border-left:1px solid ${({ theme }) => theme.sidebar.borderColor};

}

${({ selected, theme }) => selected && `
color:white;
background:${theme.sidebar.selectedIconColor};
font-weight:500;
`}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const AiSummaryIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: ${({ theme }) => theme.sidebar.selectedIconColor};

  svg {
    width: 18px;
    height: 18px;
    stroke: white;
    fill: none;
  }
`;

const RecordingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 4px;
  background: rgba(239, 68, 68, 0.15);

  span {
    font-size: 13px;
    font-weight: 600;
    color: #ef4444;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const RecordingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;
